import { VideoProviderEnum } from 'features/contents';

import { StudentPracticeExamAssessmentStatusEnum } from './StudentPracticeExamAssessmentStatusEnum';

export * from './StudentPracticeExamAssessmentStatusEnum';

export type ExamsViews = 'grid' | 'list';
export type ExamsActions = 'start' | 'finish' | null;

export enum ExamsResultCalculationTypeEnum {
  Unspecified = 0, // Not specified
  Points = 1, // Sum of points of all correct questions
  Percentual = 2, // Percentual of correct question
}

export enum ExamsMultipleAssessmentResultsChooseTypeEnum {
  Unspecified = 0, // Not specified
  BestResults = 1, // Best Results
  LatestAssessment = 2, // Latest Assessment
}

export interface ExamsTimeLeft {
  startDate: string;
  endDate: string;
  finalizeDate: string;
  now: string;
  remaining: {
    ticks: number;
    days: number;
    hours: number;
    milliseconds: number;
    minutes: number;
    seconds: number;
    totalDays: number;
    totalHours: number;
    totalMilliseconds: number;
    totalMinutes: number;
    totalSeconds: number;
  };
}

export interface ExamsItem {
  uuid: string;
  name: string;
  durationSeconds: number;
  questionCount: number;
  subjectCount: number;
  releaseDate: string;
  closeDate: string;
  productName: string;
  productClassDescription: string;
  studentPracticeExamAssessment: string;
  assessmentStartDate: string;
  assessmentEndDate: string;
  assessmentFinalizeDate: string;
  assessmentTimeLeft: ExamsTimeLeft;
  assessmentStatus: StudentPracticeExamAssessmentStatusEnum;

  allowStartPracticeExamCount: number;
  multipleAssessmentResultsChooseType: ExamsMultipleAssessmentResultsChooseTypeEnum;

  allowedCourses: string[];

  allowStartPracticeExam: boolean;
}

export interface ExamsDetailsContentsState {
  startTitle: string;
  startMessage: string;

  finishTitle: string;
  finishMessage: string;

  approvedTitle: string;
  approvedMessage: string;

  reprovedTitle: string;
  reprovedMessage: string;

  resultsTitle: string;
  resultsMessage: string;

  incompleteTitle: string;
  incompleteMessage: string;
}

export interface ExamsQuestionAnswerItemState {
  uuid: string;
  questionAnswerUuid: string;
  questionAnswerText: string;
}

export interface ExamsQuestionItemState {
  uuid: string;
  questionUuid: string;
  questionText: string;
  answers: ExamsQuestionAnswerItemState[];
  questionAnswerChoiceUuid: string;
  order: number;
  videoProvider: VideoProviderEnum;
  externalVideoProviderIdOrUrl: string;
  commentedAnswer: string;
  protectedVideoOtp: string;
  protectedVideoPlaybackInfo: string;
  protectedVideoPlayerTheme: string;
  rightQuestionAnswerChoiceUuid: string;
  rightAnswer: boolean | null;
  isQuestionInvalidated: boolean;
}

export interface ExamsDetailsState extends ExamsDetailsContentsState {
  uuid: string;
  name: string;
  description: string;

  questions: ExamsQuestionItemState[];
  durationSeconds: number;
  questionCount: number;
  subjectCount: number;
  releaseDate: string;
  closeDate: string;
  productName: string;
  productClassDescription: string;
  studentPracticeExamAssessment: string; // UUID

  assessmentStartDate: string;
  assessmentEndDate: string;
  assessmentFinalizeDate: string;
  assessmentTimeLeft: string;
  assessmentStatus: StudentPracticeExamAssessmentStatusEnum;

  allowStartPracticeExamCount: number;
  multipleAssessmentResultsChooseType: ExamsMultipleAssessmentResultsChooseTypeEnum;
  allowedCourses: string[];
}

export interface ExamsResultQuestionChoiceState {
  order: number;
  answerUuid: string;
  answerText: string;
}

export interface ExamsResultQuestionState {
  uuid: string;
  questionUuid: string;
  questionText: string;
  questionExternalId: string;
  questionAnswerChoices: ExamsResultQuestionChoiceState[];
  questionAnswerChoiceUuid: string;
  rightQuestionAnswerChoiceUuid: string;
  isQuestionInvalidated: boolean;
}

export interface ExamsResultSubjectState {
  practiceExamSubject: {
    order: number;
    uuid: string;
    practiceExamUuid: string;
    name: string;
    minimalResult: number;
    assessmentQuestionQuantity: number;
    randomizeQuestionOrder: boolean;
    randomizeAnswerOrder: boolean;
  };
  question: ExamsResultQuestionState[];
  minimalResult: number;
  result: number;
  isApproved: boolean;
  questionCount: number;
  rightQuestionAnswerCount: number;
  maximumResult: number;

  rightPercentual: number;
  rightPointsCount: number;
  totalPointsCount: number;
}

export interface ExamsResultState {
  uuid: string;
  studentUuid: string;
  practiceExamUuid: string;

  startDate: string;
  endDate: string;
  releaseDate: string;
  finalizeDate: string;
  resultDate: string;

  minimalResult: number;
  result: number;

  subject: ExamsResultSubjectState[];

  isApproved: boolean;
  isExamInvalidated: boolean;

  questionCount: number;
  rightQuestionAnswerCount: number;

  resultCalculationType: ExamsResultCalculationTypeEnum;
  maximumResult: number;

  multipleAssessmentResultsChooseType: ExamsMultipleAssessmentResultsChooseTypeEnum;

  allowStartPracticeExamCount: number;

  totalPointsCount: number;
  rightPointsCount: number;
  rightPercentual: number;
}

export interface ExamsListResponse {
  exams: ExamsItem[];
  itensPerPage: number;
  currentPage: number;
  totalPages: number;
}

export interface ExamsQuestionsListItemState {
  order: number;
  studentPracticeExamAssessmentQuestionUuid: string;
  studentPracticeExamAssessmentQuestionAnswerChoiceUuid: string | null;
  rightAnswer: boolean | null;
  isQuestionInvalidated: boolean;
}

export interface ExamsQuestionsListResponse {
  questions: ExamsQuestionsListItemState[];
}

export type ExamsState = ExamsListResponse &
  ExamsQuestionsListResponse & {
    examsDetails: ExamsDetailsState;
    examsAction: ExamsActions;
    question: ExamsQuestionItemState;
    questionCache: Record<string, ExamsQuestionItemState>;
    result: ExamsResultState;
    timeLeft: ExamsTimeLeft;
  };
